import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronLeft, ChevronRight, PartyPopper, Ticket, X } from "lucide-react";
import { getEventStatusBadge } from "../../utils/eventPermissions";
import OptionMenu from "./OptionMenu";
import { color, radius, shadow } from "../../styles/theme";

/**
 * Event hero header — image carousel, back button, status badge, options menu.
 * Ported from the mobile app's event-detail-updated/event-header/index.js
 * (structural chrome only; the SP/EA role badges there are sub-promoter/
 * business-assistant self-view indicators that don't apply to this
 * promoter-owner-facing panel).
 */
const EventHeader = ({ event, isAdmin, isAssigned, onBack, onEdit, onDelete, deleting, onToggleRoomActive, togglingRoomActive }) => {
  const { t } = useTranslation("eventDetails");
  const images = (event?.EventImages || []).map((img) => img.Url).filter(Boolean);
  const slides = images.length > 0 ? images : event?.Thumbnail ? [event.Thumbnail.replace("\\", "/")] : [];
  const [index, setIndex] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const statusBadge = getEventStatusBadge(event);

  const goPrev = (e) => { e.stopPropagation(); setIndex((i) => (i - 1 + slides.length) % slides.length); };
  const goNext = (e) => { e.stopPropagation(); setIndex((i) => (i + 1) % slides.length); };

  useEffect(() => {
    if (!showFullscreen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowFullscreen(false);
      else if (e.key === "ArrowLeft" && slides.length > 1) setIndex((i) => (i - 1 + slides.length) % slides.length);
      else if (e.key === "ArrowRight" && slides.length > 1) setIndex((i) => (i + 1) % slides.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showFullscreen, slides.length]);

  return (
    <div style={s.wrap}>
      <div style={s.imageArea}>
        {slides.length > 0 ? (
          <img
            src={slides[index]}
            alt={event?.Name}
            style={{ ...s.image, cursor: "zoom-in" }}
            onClick={() => setShowFullscreen(true)}
          />
        ) : (
          <div style={s.imagePlaceholder}><PartyPopper size={44} strokeWidth={1.5} color={color.primary400} /></div>
        )}
        <div style={s.overlay} />

        {slides.length > 1 && (
          <>
            <button type="button" style={{ ...s.navBtn, left: 12 }} onClick={goPrev} aria-label={t("eventHeader.previousImage")}>
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>
            <button type="button" style={{ ...s.navBtn, right: 12 }} onClick={goNext} aria-label={t("eventHeader.nextImage")}>
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
            <div style={s.dots}>
              {slides.map((_, i) => (
                <span key={i} style={{ ...s.dot, ...(i === index ? s.dotActive : {}) }} />
              ))}
            </div>
          </>
        )}

        <button type="button" style={s.backBtn} onClick={onBack} aria-label={t("eventHeader.back")}>
          <ArrowLeft size={14} strokeWidth={2.5} /> {t("eventHeader.back")}
        </button>

        <div style={s.topRight}>
          {isAssigned && (
            <span style={s.assignedBadge}>
              <Ticket size={12} strokeWidth={2.5} /> {t("eventHeader.subPromoterBadge")}
            </span>
          )}
          <OptionMenu
            isAdmin={isAdmin}
            isARoomEvent={event?.EventType === "ARoom"}
            isRoomClosed={Boolean(event?.IsClosed)}
            onEdit={onEdit}
            onDelete={onDelete}
            deleting={deleting}
            onToggleRoomActive={onToggleRoomActive}
            togglingRoomActive={togglingRoomActive}
          />
        </div>

        <div style={{ ...s.statusBadge, background: statusBadge.bg, color: statusBadge.color }}>
          {statusBadge.label}
        </div>
      </div>

      <div style={s.titleRow}>
        {event?.Category && <span style={s.badge}>{event.Category}</span>}
        <h2 style={s.title}>{event?.Name}</h2>
      </div>

      {showFullscreen && slides.length > 0 && (
        <div style={s.fsOverlay} onClick={() => setShowFullscreen(false)}>
          <button type="button" style={s.fsClose} onClick={() => setShowFullscreen(false)} aria-label={t("eventHeader.back")}>
            <X size={22} strokeWidth={2} />
          </button>
          {slides.length > 1 && (
            <>
              <button type="button" style={{ ...s.fsNavBtn, left: 20 }} onClick={goPrev}>
                <ChevronLeft size={22} strokeWidth={2.5} />
              </button>
              <button type="button" style={{ ...s.fsNavBtn, right: 20 }} onClick={goNext}>
                <ChevronRight size={22} strokeWidth={2.5} />
              </button>
              <div style={s.fsCounter}>{index + 1} / {slides.length}</div>
            </>
          )}
          <img src={slides[index]} alt={event?.Name} style={s.fsImage} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

const s = {
  wrap: { background: color.surface, borderRadius: radius.lg, overflow: "hidden", boxShadow: shadow.sm, marginBottom: 24, border: `1px solid ${color.border}` },
  imageArea: { position: "relative", width: "100%", height: 260, background: color.gray900 },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  imagePlaceholder: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: color.primary50 },
  overlay: { position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)", pointerEvents: "none" },
  navBtn: {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    width: 30, height: 30, borderRadius: "50%", border: "none",
    background: "rgba(20,21,40,0.5)", color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  dots: { position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 6 },
  dot: { width: 5, height: 5, borderRadius: "50%", background: "rgba(255,255,255,0.5)" },
  dotActive: { background: "#fff", width: 14, borderRadius: 3 },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    position: "absolute", top: 14, left: 14, padding: "6px 12px 6px 10px", borderRadius: radius.pill,
    border: "none", background: "rgba(20,21,40,0.5)", color: "#fff", fontSize: 12.5,
    fontWeight: 600, cursor: "pointer",
  },
  topRight: { position: "absolute", top: 14, right: 14, display: "flex", alignItems: "center", gap: 8 },
  assignedBadge: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "rgba(255,255,255,0.92)", color: color.warning500, fontSize: 11, fontWeight: 700,
    padding: "5px 11px", borderRadius: radius.pill,
  },
  statusBadge: {
    position: "absolute", bottom: 14, left: 14, fontSize: 11, fontWeight: 700,
    padding: "5px 12px", borderRadius: radius.pill, textTransform: "uppercase", letterSpacing: 0.4,
  },
  titleRow: { padding: "18px 22px 22px" },
  badge: {
    display: "inline-block", background: color.primary50, color: color.primary600, fontSize: 11,
    fontWeight: 700, padding: "3px 10px", borderRadius: radius.pill, textTransform: "uppercase",
    letterSpacing: 0.5, marginBottom: 10,
  },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.3 },
  fsOverlay: {
    position: "fixed", inset: 0, background: "rgba(10,10,16,0.92)", zIndex: 1200,
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out",
  },
  fsClose: {
    position: "absolute", top: 18, right: 18, width: 40, height: 40, borderRadius: "50%",
    border: "none", background: "rgba(255,255,255,0.12)", color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  fsNavBtn: {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    width: 42, height: 42, borderRadius: "50%", border: "none",
    background: "rgba(255,255,255,0.12)", color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  fsCounter: {
    position: "absolute", bottom: 22, left: "50%", transform: "translateX(-50%)",
    fontSize: 12.5, fontWeight: 600, color: "#fff", background: "rgba(255,255,255,0.12)",
    padding: "5px 12px", borderRadius: radius.pill,
  },
  fsImage: { maxWidth: "90vw", maxHeight: "88vh", objectFit: "contain", cursor: "default", borderRadius: radius.sm },
};

export default EventHeader;
